import { useState, useRef, useEffect } from "react";
import "./style.css";
import PeriodicTablePanel from "./PeriodicTablePanel";
import SummaryBottomSheet from "./SummaryBottomSheet";
import GenericModal from "../Modal/GenericModal";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { useNavigate } from "react-router-dom";
import ElementHeader from "./ElementHeader";
import { fetchElementsIntroConfig } from "../../Services/contentConfig";
const ArisPeriodicTableLayout = () => {
  const [activeTabs, setActiveTabs] = useState(["All"]); // Legacy: kept for downstream highlighting logic
  const [activeView, setActiveView] = useState("Domains"); // "Domains" | "Levels" — replaces the old filter pills
  const [summaries, setSummaries] = useState(null); // API summaries payload
  const [summaryText, setSummaryText] = useState(""); // text shown on right
  const [summaryTitle, setSummaryTitle] = useState("Aris Elements"); // right panel heading
  const [levelFocus, setLevelFocus] = useState(null); // which level is focused for highlighting
  const [showDomainCarrotFor, setShowDomainCarrotFor] = useState(null);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileSheetTitle, setMobileSheetTitle] = useState("");
  const [mobileSheetText, setMobileSheetText] = useState("");
  const [lastClickedDomain, setLastClickedDomain] = useState(null);
  const [mobileSheetOrigin, setMobileSheetOrigin] = useState(null); // 'level' | 'tab' | null
  const [summaryStatus, setSummaryStatus] = useState(null);
  const [summariesLoaded, setSummariesLoaded] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [elementsData, setElementsData] = useState(null);
  const [introConfig, setIntroConfig] = useState(null);
  const [mobileSheetScopeKey, setMobileSheetScopeKey] = useState(null);
  const leftRef = useRef(null);
  const containerRef = useRef(null);
  const { setHeaderContent } = useHeader();
  const [isMobileView, setIsMobileView] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobileView(width <= 600); // Adjusted breakpoint for mobile view
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    setHeaderContent(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchElementsIntroConfig()
      .then((cfg) => {
        if (mounted) setIntroConfig(cfg);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Shared categories data
  const categories = {
    identity: { name: "Identity", color: "#3B82F6", label: "Identity" },
    cognition: { name: "Cognition", color: "#10B981", label: "Cognition" },
    judgement: { name: "Judgement", color: "#EC4899", label: "Judgment" },
    resolution: { name: "Resolution", color: "#8B5CF6", label: "Resolution" },
    motivation: { name: "Motivation", color: "#F59E0B", label: "Motives" },
  };

  const onBackClick = () => {
    navigate("/dashboard");
  };

  // Map the section label emitted by PeriodicTablePanel
  // (DOMAIN_DISPLAY[*].label) to the API key in summaries.domains[*]. The
  // labels diverge from the API keys for Judgment (label "Judgment" / API
  // "Judgment") and Motivation (label "Motives" / API "Motivation").
  const domainApiKeyMap = {
    Identity: "Identity",
    Cognition: "Cognition",
    Judgment: "Judgment",
    Resolution: "Resolution",
    Motives: "Motivation",
  };

  const handleSummariesLoaded = (data) => {
    // data: { summaries, summary_status, elements_data }
    setSummaries(data?.summaries || null);
    setSummaryStatus(data?.summary_status || null);
    setElementsData(data?.elements_data || null);
    const initialContent = data?.summaries?.this_is_you?.content || "";
    const initialTitle = data?.summaries?.this_is_you?.title;
    setSummaryText(initialContent);
    setLevelFocus(null);
    // Prefer API-provided title; fallback to personalized/local build
    if (initialTitle) {
      setSummaryTitle(initialTitle);
    } else {
      let firstName = "";
      try {
        const storedProfile = localStorage.getItem("profile");
        if (storedProfile && storedProfile !== "undefined") {
          const parsed = JSON.parse(storedProfile);
          firstName = parsed?.first_name || "";
        }
      } catch (e) {
        // ignore parsing errors and fall back
      }
      setSummaryTitle(firstName ? `This Is You, ${firstName}` : "This Is You");
    }
    setShowDomainCarrotFor(null);
    setSummariesLoaded(true);
  };

  const handleGenerateSummaries = async () => {
    try {
      setIsGenerating(true);
      const { generateElementsSummary } = await import("../../Services/elements");
      await generateElementsSummary();
      // Optimistically set to queued and trigger refresh
      setSummaryStatus("queued");
      setRefreshSignal((s) => s + 1);
    } catch (e) {
      console.error("Generate summaries failed", e);
      setSummaryStatus("error");
    } finally {
      setIsGenerating(false);
    }
  };

  const updateSummaryFromDomain = (domainLabel) => {
    if (!summaries?.domains) return;
    const key = domainApiKeyMap[domainLabel];
    const domainObj = summaries.domains[key];
    const text = domainObj?.content || summaries?.this_is_you?.content || "";
    setSummaryText(text);
    setLastClickedDomain(domainLabel);
    setLevelFocus(null); // clear level focus when a domain is selected
    setSummaryTitle(domainObj?.title || domainLabel);
  };

  const updateSummaryFromLevel = (levelName) => {
    if (!summaries?.levels) return;
    const levelObj = summaries.levels[levelName];
    const text = levelObj?.content || summaries?.this_is_you?.content || "";
    setSummaryText(text);
    setLevelFocus(levelName);
    // deselect any domain filter and tabs (including All)
    setActiveTabs([]);
    setSummaryTitle(levelObj?.title || `${levelName} Aris Elements`);
  };

  const openMobileSheet = (title, text, origin = 'level', scopeKey = null) => {
    setMobileSheetTitle(title);
    setMobileSheetText(text || "");
    setMobileSheetOrigin(origin);
    setMobileSheetScopeKey(scopeKey);
    setMobileSheetOpen(true);
  };

  const handlePickTopic = (questionText, isOpenConversation) => {
    const params = new URLSearchParams();
    params.set("elements", "1");

    // Determine scope/domain/level from the active sheet context.
    let inferredScope = null;
    let inferredLevel = null;
    let inferredDomain = null;

    if (mobileSheetOrigin === "level" && mobileSheetScopeKey) {
      inferredLevel = mobileSheetScopeKey;
      inferredScope = "level";
    } else if (mobileSheetOrigin === "tab" && mobileSheetScopeKey) {
      inferredDomain = mobileSheetScopeKey;
      inferredScope = "domain";
    } else if (levelFocus) {
      inferredLevel = String(levelFocus);
      inferredScope = "level";
    } else if (lastClickedDomain) {
      const d = (lastClickedDomain || "").toLowerCase();
      if (["identity", "cognition", "judgment", "resolution", "motivation"].includes(d)) {
        inferredDomain = d;
        inferredScope = "domain";
      }
    }

    if (!inferredScope) inferredScope = "this_is_you";

    params.set("scope", inferredScope);

    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

    // Respect the user's coaching mode — voice goes to /coach/private,
    // keyboard goes to /chat. Mirrors ElementDetail's getCoachingMode().
    let modality = "chat";
    try {
      const v = (localStorage.getItem("coaching_mode") || "").trim().toLowerCase();
      if (v === "voice") modality = "voice";
    } catch (_) {}

    params.set("modality", modality);

    if (inferredScope === "domain" && inferredDomain) {
      const dom = cap(inferredDomain);
      params.set("domain", dom);
      params.set("topic", dom);
      params.set("conversation_topic", isOpenConversation ? "open" : "topic");
      params.set("question", questionText);
    } else if (inferredScope === "level" && inferredLevel) {
      const lvl = cap(inferredLevel);
      params.set("level", lvl);
      params.set("topic", lvl);
      params.set("conversation_topic", isOpenConversation ? "open" : "topic");
      params.set("question", questionText);
    } else if (questionText) {
      params.set("question", questionText);
    }

    // Auto-fire the first turn so the conversation opens with the user's
    // selected question already submitted (no in-chat option pills).
    params.set("element_action", "coaching");

    const basePath = modality === "voice" ? "/coach/private" : "/chat";
    navigate(`${basePath}?${params.toString()}`);
  };

  const resolveSheetOptions = () => {
    if (!introConfig || !mobileSheetScopeKey) return [];
    if (mobileSheetOrigin === "tab") {
      return introConfig?.domains?.[mobileSheetScopeKey]?.options || [];
    }
    if (mobileSheetOrigin === "level") {
      return introConfig?.levels?.[mobileSheetScopeKey]?.options || [];
    }
    return [];
  };

  const sheetEyebrow = mobileSheetOrigin === "level"
    ? "Level"
    : mobileSheetOrigin === "tab"
    ? "Domain"
    : null;

  const handleLevelHeaderClick = (levelName) => {
    // Mobile: ignore header taps; Tablet/Desktop: behave like desktop
    if (isMobileView) return;
    updateSummaryFromLevel(levelName);
  };

  const handleLevelCarrotClick = (levelName) => {
    const levelObj = summaries?.levels?.[levelName];
    const txt = levelObj?.content || summaryText;
    const ttl = levelObj?.title || `${levelName} Aris Elements`;
    openMobileSheet(ttl, txt, 'level', String(levelName || "").toLowerCase());
  };

  useEffect(() => {
    if (!summaries) return;
    // Do not override when a level is focused
    if (levelFocus) return;
    const current = activeTabs[0];
    if (current && current !== "All" && summaries?.domains) {
      updateSummaryFromDomain(current);
    } else if (current === "All" && summaries?.this_is_you) {
      setSummaryText(summaries.this_is_you?.content || "");
    }
  }, [summaries, activeTabs, levelFocus]);

  // keep default as this_is_you; do not auto-select a level

  
  return (
      <div className="element-overall-container">
        <ElementHeader
          onBackClick={onBackClick}
          isMobileView={isMobileView}
          hideHistoryButton
        />
        <div ref={containerRef} className="aris-container">
          <div id="section-title" className="title-section">
            <div className="title-content-container">
              <div className="title-main-left">
                <div className="title-main-content">
                  <h1>Explore Your Elements</h1>
                </div>
              </div>
              <div className="title-description">
                <p>Your system of how you operate across your life</p>
              </div>
            </div>
          </div>
          {/* Domains | Levels toggle — replaces the old All/Identity/Cognition/Judgment/Resolution/Motives pill row */}
          <div className="aris-filter-row">
            <div className="aris-view-toggle" role="tablist" aria-label="Group elements by">
              <button
                type="button"
                role="tab"
                aria-selected={activeView === "Domains"}
                className={`aris-view-toggle-btn ${activeView === "Domains" ? "is-active" : ""}`}
                onClick={() => {
                  setActiveView("Domains");
                  setActiveTabs(["All"]);
                  setLevelFocus(null);
                  if (summaries?.this_is_you?.content) setSummaryText(summaries.this_is_you.content);
                  setSummaryTitle(summaries?.this_is_you?.title || "This Is You");
                }}
              >
                Domains
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeView === "Levels"}
                className={`aris-view-toggle-btn ${activeView === "Levels" ? "is-active" : ""}`}
                onClick={() => {
                  setActiveView("Levels");
                  setActiveTabs(["All"]);
                  setLevelFocus(null);
                  if (summaries?.this_is_you?.content) setSummaryText(summaries.this_is_you.content);
                  setSummaryTitle(summaries?.this_is_you?.title || "This Is You");
                }}
              >
                Levels
              </button>
            </div>
            <p className="aris-view-toggle-caption">
              {activeView === "Domains"
                ? "See how your elements come together"
                : "Your system across the levels of who you are"}
            </p>
          </div>

          {/* Content Area — periodic table is centered full-width.
              The right summary panel was removed; domain/level summaries
              now open through the (i) icon overlay below. */}
          <div className="aris-content-area">
            <div className="aris-panels-container">
              <div className="aris-left-panel aris-left-panel-centered">
                <PeriodicTablePanel
                  width={100}
                  categories={categories}
                  activeTabs={activeTabs}
                  groupBy={activeView}
                  onDomainHeaderClick={updateSummaryFromDomain}
                  onDomainCarrotClick={(domainName) => {
                    const key = domainApiKeyMap[domainName];
                    const domObj = summaries?.domains?.[key];
                    const txt = domObj?.content || summaryText;
                    openMobileSheet(
                      domObj?.title || domainName,
                      txt,
                      'tab',
                      String(domainName || "").toLowerCase()
                    );
                  }}
                  leftRef={leftRef}
                  onSummariesLoaded={handleSummariesLoaded}
                  onLevelHeaderClick={handleLevelHeaderClick}
                  onLevelCarrotClick={handleLevelCarrotClick}
                  levelFocus={levelFocus}
                  isMobile={isMobileView}
                  refreshSignal={refreshSignal}
                  summariesStatus={summaryStatus}
                  onGenerateSummaries={handleGenerateSummaries}
                />
              </div>
            </div>
          </div>
      </div>
      {/* Summary overlay — opens whenever someone taps an (i) icon, on any
          viewport. Replaces the old desktop-side panel. */}
      <SummaryBottomSheet
        open={mobileSheetOpen}
        eyebrow={sheetEyebrow}
        title={mobileSheetTitle}
        text={mobileSheetText}
        options={resolveSheetOptions()}
        onPickOption={handlePickTopic}
        onClose={() => {
          setMobileSheetOpen(false);
          if (mobileSheetOrigin === 'level') {
            setActiveTabs([]);
            setLevelFocus(null);
            if (summaries?.this_is_you?.content) setSummaryText(summaries.this_is_you.content);
            setSummaryTitle(summaries?.this_is_you?.title || "This Is You");
          }
          setMobileSheetOrigin(null);
          setMobileSheetScopeKey(null);
        }}
        showGenerateButton={!summaryStatus || summaryStatus === 'idle' || summaryStatus === 'error'}
        onGenerateSummaries={handleGenerateSummaries}
        generating={isGenerating}
        summariesLoaded={summariesLoaded}
        summaryStatus={summaryStatus}
      />
      
      {/* Unlock modal for locked categories */}
      <GenericModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        heading="Unlock the Table of the Self"
        primaryLabel=""
        secondaryLabel=""
        onPrimary={() => setShowUnlockModal(false)}
        onSecondary={() => setShowUnlockModal(false)}
        disableCloseButton={false}
        maxWidth="500px"
      >
        <p style={{ 
          color: '#667085', 
          fontSize: '16px', 
          lineHeight: '24px',
          fontFamily: 'Urbanist',
          margin: '0',
          maxWidth: '268px'
        }}>
          Complete all exercises in the mobile app to unlock the full Table of the Self.
        </p>
      </GenericModal>
    </div>
  );
};

export default ArisPeriodicTableLayout;

